import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import type { WidgetTableProps } from './types';

/**
 * WidgetTable
 * - Purely presentational widget table
 * - Correct sticky header behavior (shadcn-safe)
 * - Scrollable in both axes
 */
export function WidgetTable<T>({
  data,
  columns,
  onRowClick,
  emptyState = 'No data',
  error,
  rowClassName,
  className,
  showShadow = true,
  getRowId,
  highlightedRowId,
}: WidgetTableProps<T>) {
  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!highlightedRowId) return;
    if (!highlightedRowRef.current) return;

    highlightedRowRef.current.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [highlightedRowId]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-[180px] text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[120px] text-sm text-muted-foreground">
        {emptyState}
      </div>
    );
  }

  const clickable = !!onRowClick;

  return (
    <div
      className={cn(
        'relative rounded-md bg-card border border-card-border overflow-hidden max-h-[320px]',
        showShadow && 'shadow-card',
        // Allow parent widgets to control sizing (e.g. flex-1, min-h-0)
        // while retaining a reasonable max height for standalone usage.
        className
      )}
    >
      {/* SCROLL CONTAINER — must directly wrap table */}
      <div className="relative isolate h-full overflow-auto">
        <Table
          addParentWrapper={false}
          className="min-w-full border-separate border-spacing-0 text-xs"
        >
          <TableHeader>
            <TableRow className="!bg-transparent hover:!bg-transparent">
              {columns.map(col => (
                <TableHead
                  key={col.id}
                  className={cn(
                    'sticky top-0 z-20',
                    'bg-muted',
                    'after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border',
                    'px-3 font-medium text-foreground/80 whitespace-nowrap',
                    col.className
                  )}
                >
                  {typeof col.header === 'function' ? col.header() : col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((row, i) => {
              const rowId = getRowId ? getRowId(row) : undefined;
              const isHighlighted =
                !!rowId && !!highlightedRowId && rowId === highlightedRowId;

              return (
                <TableRow
                  key={rowId ?? i}
                  ref={isHighlighted ? highlightedRowRef : undefined}
                  className={cn(
                    rowClassName?.(row),
                    clickable &&
                      'cursor-pointer hover:bg-muted/50 transition-colors',
                    !clickable && 'hover:bg-muted/50'
                  )}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  style={clickable ? { userSelect: 'none' } : undefined}
                >
                  {columns.map(col => (
                    <TableCell
                      key={col.id}
                      className={cn(
                        'px-3 py-2 whitespace-nowrap',
                        col.className
                      )}
                    >
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
